/**
 * Login Page
 *
 * In-app email/password + TOTP MFA login via Google Identity Platform.
 * The agency GCIP tenant is resolved from the user's email domain
 * (GET /api/tenants/by-domain/{domain}); tenant-specific deep links use
 * /login/[slug]. See lib/auth-client-gcip.ts and CJIS_EXCEPTIONS.md PE-21.
 */

'use client';

import { Suspense, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { getAuthClient } from '@/lib/auth-client';
import { getAuthResetMessage } from '@/lib/auth-reset';
import { sanitizeRedirect } from '@/lib/auth-routes';
import { GcipLoginCard } from '@/components/auth/GcipLoginCard';
import { logger } from '@/lib/logger';


function LoginForm() {
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();
  const redirect = sanitizeRedirect(searchParams.get('redirect'));
  // The backend refused this account (lib/no-app-access.ts) and the terminal
  // reset that brought us here signed Firebase out. Skip the existing-session
  // sync as well: if that sign-out failed, the sync would route the user back to
  // the dashboard to be refused again — the loop the terminal reset breaks.
  const noAppAccess =
    searchParams.get('reset') === '1' && searchParams.get('reason') === 'no-app-access';

  const [syncing, setSyncing] = useState(!noAppAccess);
  const syncAttempted = useRef(false);

  // If the user already has a live Firebase session (e.g. returned to /login
  // in a new tab), re-establish the __session cookie and route them in rather
  // than making them sign in again.
  useEffect(() => {
    if (noAppAccess) return;

    const client = getAuthClient();
    let mounted = true;

    const unsubscribe = client.onAuthStateChanged(async (user) => {
      if (!user) {
        if (mounted) setSyncing(false);
        return;
      }
      if (syncAttempted.current) return;
      syncAttempted.current = true;
      try {
        const token = await client.getIdToken(true);
        if (!token) throw new Error('No token');
        const res = await fetch('/api/auth/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!res.ok) throw new Error('session sync failed');
        queryClient.clear();
        // gh #1758: full document load, same reasoning as finishAuthenticated
        // in components/auth/GcipLoginCard.tsx — a client-side transition hands
        // the destination an AuthProvider that is still mid-resolution from the
        // token events this sync just fired, which strands it on the
        // unauthenticated dead-end. `redirect` is sanitizeRedirect()'d above.
        if (mounted) {
          window.location.href = redirect;
        }
      } catch (err) {
        logger.warn('[LoginPage] Existing-session sync failed; showing login form', err);
        if (mounted) setSyncing(false);
      }
    });

    // No callback within a tick → no existing session; show the form.
    const t = setTimeout(() => {
      if (mounted && !syncAttempted.current) setSyncing(false);
    }, 1500);

    return () => {
      mounted = false;
      clearTimeout(t);
      unsubscribe();
    };
  }, [noAppAccess, queryClient, redirect]);

  if (syncing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <svg className="h-8 w-8 animate-spin text-[#20556f]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  return (
    <GcipLoginCard
      redirect={redirect}
      initialError={noAppAccess ? getAuthResetMessage('no-app-access') : undefined}
    />
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
      <LoginForm />
    </Suspense>
  );
}
